// src/hooks/useVibeCoding.js
import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

export const useVibeCoding = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [projectPath, setProjectPath] = useState('');
  const [projectFiles, setProjectFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [includeCurrentFiles, setIncludeCurrentFiles] = useState(false);
  const [allProjectFiles, setAllProjectFiles] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [showFileExplorer, setShowFileExplorer] = useState(false);

  const userId = 'vibe_user';

  // Load saved project path from localStorage
  useEffect(() => {
    const savedPath = localStorage.getItem('vibe_coding_project_path');
    if (savedPath) {
      setProjectPath(savedPath);
      window.vibeCurrentProject = savedPath;
    }
  }, []);
  
  // Update global project path when it changes
  useEffect(() => {
    window.vibeCurrentProject = projectPath;
  }, [projectPath]);

  // Set default model for vibe coding
  useEffect(() => {
    const fetchDefaultModel = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/models/vibecoding`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.defaultModel && !selectedModel) {
          setSelectedModel(data.defaultModel);
        }
      } catch (error) {
        console.error('Failed to fetch default model:', error);
        setSelectedModel('coder'); // fallback
      }
    };
    
    if (!selectedModel) {
      fetchDefaultModel();
    }
  }, [selectedModel]);

  // Check connection and load data
  useEffect(() => {
    checkConnection();
    loadChatHistory();
  }, []);

  // Fetch project files when connected and path changes
  useEffect(() => {
    if (isConnected && projectPath) {
      fetchProjectFiles(projectPath);
      if (includeCurrentFiles) {
        fetchAllProjectFiles(projectPath);
      }
    }
  }, [isConnected, projectPath, includeCurrentFiles]);
  
  // Fetch selected files content for token calculation
  useEffect(() => {
    if (isConnected && projectPath && selectedFiles.length > 0) {
      fetchSelectedFilesContent();
    }
  }, [isConnected, projectPath, selectedFiles]);
  
  const fetchSelectedFilesContent = async () => {
    if (!projectPath || selectedFiles.length === 0) {
      setAllProjectFiles('');
      return;
    }
    
    try {
      const response = await fetch(
        `http://localhost:3007/api/file-contents?project=${encodeURIComponent(projectPath)}&filesNeeded=${encodeURIComponent(JSON.stringify(selectedFiles))}`,
        { headers: { 'Content-Type': 'application/json' }, mode: 'cors' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && (data.files || data.contents)) {
          const fileData = data.files || data.contents;
          setAllProjectFiles(JSON.stringify(data.contents));
          console.log('Set selected files content for token calculation:', Object.keys(fileData).length, 'files');
        }
      }
    } catch (error) {
      console.error('Failed to fetch selected files content:', error);
    }
  };

  const checkConnection = async () => {
    try {
      const response = await fetch('http://localhost:3007/api/license-status', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors'
      });
      setIsConnected(response.ok);
    } catch (error) {
      setIsConnected(false);
      console.error('Connection failed:', error);
    }
  };

  const fetchProjectFiles = async (path) => {
    if (!path) return;
    try {
      const response = await fetch(`http://localhost:3007/api/file-paths?project=${encodeURIComponent(path)}`, {
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProjectFiles(buildFileTree(data.filePaths || []));
          localStorage.setItem('vibe_coding_project_path', path);
        }
      }
    } catch (error) {
      console.error('Failed to fetch project files:', error);
    }
  };

  // Reusable function to fetch project files content
  const fetchProjectFilesContent = async (path) => {
    if (!path) return null;
    try {
      const response = await fetch(`http://localhost:3007/api/file-contents?project=${encodeURIComponent(path)}`, {
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Raw backend response:', data);
        if (data.success && (data.files || data.contents)) {
          const fileData = data.files || data.contents;
          console.log('File data type:', typeof fileData, 'Is array:', Array.isArray(fileData));
          console.log('File data sample:', Object.keys(fileData).slice(0, 5));
          return fileData;
        }
      }
    } catch (error) {
      console.error('Failed to fetch project files content:', error);
    }
    return null;
  };

  const fetchAllProjectFiles = async (path) => {
    if (!path) return;
    const fileData = await fetchProjectFilesContent(path);
    if (fileData) {
      // Format files properly without JSON.stringify corruption
    
      setAllProjectFiles(JSON.stringify(fileData) );
      // console.log('Set allProjectFiles:', Object.keys(fileData).length, 'files, size:', filesContent.length, 'chars');
    }
  };

  const buildFileTree = (filePaths) => {
    const tree = {};
    filePaths.forEach(filePath => {
      const parts = filePath.split('/');
      let current = tree;
      parts.forEach((part, index) => {
        if (!current[part]) {
          current[part] = {
            name: part,
            path: parts.slice(0, index + 1).join('/'),
            isFile: index === parts.length - 1,
            children: {}
          };
        }
        current = current[part].children;
      });
    });
    return tree;
  };

  const loadChatHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history?mode=vibecoding`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setChatHistory(data.chats || []);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const saveChatHistory = async (chatId, messages, title, totalTokens = 0, totalCost = 0) => {
    try {
      const chatData = {
        id: chatId,
        title: title || 'New Chat',
        messages: messages,
        projectPath: projectPath,
        totalTokens: totalTokens,
        totalCost: totalCost,
        mode: 'vibecoding'
      };
      window.conversationMessages=chatData;
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(chatData)
      });
      
      if (response.ok) {
        loadChatHistory(); // Refresh chat history
      }
    } catch (error) {
      console.error('Failed to save chat history:', error);
    }
  };

  const getChatTitle = (messages) => {
    const userMessages = messages.filter(msg => msg.role === 'user' && !msg.isSystemMessage);
    if (userMessages.length > 0) {
      return userMessages[0].content.slice(0, 30) + (userMessages[0].content.length > 30 ? '...' : '');
    }
    return 'New Chat';
  };

  const sendMessage = async (attachments = [], setAttachments = null) => {
    if (!inputMessage.trim() || !selectedModel || !isConnected) return;
    
    // Clear selected files and close panels after sending
    const clearAfterSend = () => {
      setSelectedFiles([]);
      setShowFileExplorer(false);
      setShowProjectSelector(false);
    };
    
    // If no project path, show a helpful message
    if (!projectPath) {
      const helpMessage = {
        role: 'assistant',
        content: '📁 **No Project Selected**\n\nTo get the most out of VibeCoding, please select a project first using the 📁 button. You can still chat without a project, but I won\'t have access to your code files.',
        timestamp: new Date().toISOString(),
        isSystemMessage: true
      };
      setMessages(prev => [...prev, helpMessage]);
    }
    
    let finalUserMessage = inputMessage;
    
    // Add project context if enabled OR if files are selected
    if ((includeCurrentFiles || selectedFiles.length > 0) && projectPath) {
      try {
        if (includeCurrentFiles) {
          // Fetch all project files
          const response = await fetch(`http://localhost:3007/api/file-contents?project=${encodeURIComponent(projectPath)}`, {
            headers: { 'Content-Type': 'application/json' },
            mode: 'cors'
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && (data.files || data.contents)) {
              const fileData = data.files || data.contents;
              // Send files with proper content formatting
              // let filesContent = '\n\nProject Files:\n ' + data.contents;
             
              finalUserMessage = `${inputMessage}${data.contents}`;
              // Cache for token calculation
              window.vibeProjectFilesCache = { files: fileData, timestamp: Date.now() };
            }
          }
        } else if (selectedFiles.length > 0) {
          // Fetch only selected files
          const response = await fetch(
            `http://localhost:3007/api/file-contents?project=${encodeURIComponent(projectPath)}&filesNeeded=${encodeURIComponent(JSON.stringify(selectedFiles))}`,
            { headers: { 'Content-Type': 'application/json' }, mode: 'cors' }
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success && (data.files || data.contents)) {
              const fileData =JSON.stringify( data.files || data.contents);
              // Send files with proper content formatting
              // let filesContent = '\n\context Files:\n' +fileData;
              finalUserMessage = `${inputMessage}${fileData}`;
              // Cache for token calculation
              window.vibeProjectFilesCache = { files: fileData, timestamp: Date.now() };
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch project files:', error);
      }
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      apiContent: finalUserMessage, // Store the full message content sent to API
      attachments: [...attachments],
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    // Create or update chat history
    let chatId = currentChatId;
    if (!chatId) {
      chatId = 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      setCurrentChatId(chatId);
    }
    saveChatHistory(chatId, newMessages, getChatTitle(newMessages));

    setInputMessage('');
    if (setAttachments) {
      setAttachments([]);
    }
    setIsLoading(true);
    setIncludeCurrentFiles(false);
    
    // Clear selections and close panels
    clearAfterSend();

    try {
      // Build conversation with full API content (including file contents)
      const conversationMessages = messages.filter(msg => !msg.isSystemMessage).map(msg => ({
        role: msg.role,
        content: msg.apiContent || msg.content // Use apiContent if available for full context
      }));
      // window.conversationMessages=conversationMessages;
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/vibecoding/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          model: selectedModel,
          message: finalUserMessage,
          conversation: conversationMessages,
          attachments: attachments.map(att => ({
            type: att.type,
            name: att.name,
            data: att.data,
            size: att.size,
            mimeType: att.mimeType || att.file?.type
          })),
          projectContext: projectPath ? `\n\nCurrent project: ${projectPath}` : ''
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        let aiMessage;
        
        if (data.type === 'executable') {
          // Store both explanation and JSON for context
          // const fullAIResponse = `${data.explanationText || 'Processing your request...'}\n\nExecutable JSON:\n${data.executableJson}`;
          const fullAIResponse = data.fullAIResponse;
          
          aiMessage = {
            role: 'assistant',
            content: data.explanationText || 'Processing your request...',
            apiContent: fullAIResponse, // Store full response including JSON for context
            timestamp: new Date().toISOString()
          };
          
          // Execution will happen after message is set
        } else {
          // Regular text response
          aiMessage = {
            role: 'assistant',
            content: data.message,
            apiContent: data.message, // AI responses are the same for display and API
            timestamp: new Date().toISOString()
          };
        }
        
        // Add AI message to conversation
        const updatedMessages = [...newMessages, aiMessage];
        setMessages(updatedMessages);
        
        // Update chat history with complete conversation including new AI response
        saveChatHistory(chatId, updatedMessages, getChatTitle(updatedMessages));
        
        // Execute code if needed (after messages are properly set)
        if (data.type === 'executable' && data.executableJson) {
          // Execute the JSON silently in background
          executeCode(JSON.stringify(data.executableJson));
        }
        
        // Update token usage and cost tracking
        if (data.usage) {
          const { total_tokens } = data.usage;
          if (window.consumeTokens) {
            window.consumeTokens(total_tokens);
          }
        }
        
        // Update user stats if provided
        if (data.userStats) {
          window.dispatchEvent(new CustomEvent('userStatsUpdated', {
            detail: {
              balance: data.userStats.balance,
              totalTokensUsed: data.userStats.totalTokensUsed,
              totalCost: data.userStats.totalCost
            }
          }));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorText = 'Failed to communicate with AI service.';
        if (errorData.error) {
          errorText = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.message) {
          errorText = errorData.message;
        } else {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        const errorMessage = {
          role: 'assistant',
          content: `🚨 **API Communication Error**\n\n${errorText}`,
          timestamp: new Date().toISOString(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        role: 'assistant',
        content: `🚨 **Connection Error**\n\n${error.message}\n\nPlease check your internet connection and try again.`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
    
    setIsLoading(false);
  };

  const executeCode = async (jsonResponse) => {
    console.log('executeCode called with:', jsonResponse);
    
    const buildingMessage = {
      id: `build_${Date.now()}`,
      role: 'assistant',
      content: `🔨 **Building...**\n\nApplying changes and running build process...`,
      timestamp: new Date().toISOString(),
      isSystemMessage: true
    };
    
    setMessages(prev => {
      const updatedMessages = [...prev, buildingMessage];
      console.log('Added building message, total messages:', updatedMessages.length);
      // Save to chat history immediately
      setTimeout(() => {
        saveChatHistory(currentChatId, updatedMessages, getChatTitle(updatedMessages));
      }, 100);
      return updatedMessages;
    });

    try {
      console.log('Executing build with project:', projectPath);
      
      const response = await fetch('http://localhost:3007/api/execute-build', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
          result: jsonResponse,
          projectName: projectPath || 'TestLab'
        })
      });

      console.log('Execute build response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Execute build result:', result);
      
      let statusMessage;
      
      if (result.success) {
        statusMessage = {
          id: `build_success_${Date.now()}`,
          role: 'assistant',
          content: `✅ **Build Complete!**\n\nYour changes have been successfully applied.${result.filesModified ? `\n\nFiles modified: ${result.filesModified}` : ''}${result.commandsExecuted ? `\nCommands executed: ${result.commandsExecuted}` : ''}`,
          timestamp: new Date().toISOString(),
          isSystemMessage: true
        };
      } else {
        const errorDetails = result.error ? `\n\nError: ${result.error}` : '';
        const commandOutput = result.output ? `\n\nOutput: ${result.output}` : '';
        
        statusMessage = {
          id: `build_error_${Date.now()}`,
          role: 'assistant',
          content: `❌ **Build Failed**\n\nBuild process encountered errors.${errorDetails}${commandOutput}`,
          timestamp: new Date().toISOString(),
          isSystemMessage: true,
          isError: true
        };
      }
      
      setMessages(prev => {
        const updatedMessages = [...prev, statusMessage];
        console.log('Added status message, total messages:', updatedMessages.length);
        // Save to chat history immediately
        setTimeout(() => {
          saveChatHistory(currentChatId, updatedMessages, getChatTitle(updatedMessages));
        }, 100);
        return updatedMessages;
      });
      
    } catch (error) {
      console.error('Execute build error:', error);
      
      const errorMessage = {
        id: `build_connection_error_${Date.now()}`,
        role: 'assistant',
        content: `🚨 **Connection Error**\n\nCannot connect to the development server at localhost:3007.\n\nError: ${error.message}\n\nPlease ensure Brimind VIBE is running.`,
        timestamp: new Date().toISOString(),
        isSystemMessage: true,
        isError: true
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        console.log('Added error message, total messages:', updatedMessages.length);
        // Save to chat history immediately
        setTimeout(() => {
          saveChatHistory(currentChatId, updatedMessages, getChatTitle(updatedMessages));
        }, 100);
        return updatedMessages;
      });
    }
  };

  const createNewChat = () => {
    const newChatId = 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    setCurrentChatId(newChatId);
    setMessages([]);
  };

  const loadChat = async (chatId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history/${chatId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.chat) {
          setCurrentChatId(chatId);
          // Restore messages with their full API content preserved
          setMessages(data.chat.messages);
          if (data.chat.projectPath) {
            setProjectPath(data.chat.projectPath);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load chat:', error);
    }
  };

  const deleteChat = async (chatId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history/${chatId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        loadChatHistory(); // Refresh chat history
        if (currentChatId === chatId) {
          createNewChat();
        }
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  };

  return {
    // State
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    selectedModel,
    setSelectedModel,
    isLoading,
    isConnected,
    projectPath,
    setProjectPath,
    projectFiles,
    selectedFiles,
    setSelectedFiles,
    includeCurrentFiles,
    setIncludeCurrentFiles,
    allProjectFiles,
    chatHistory,
    currentChatId,
    // UI State
    showProjectSelector,
    setShowProjectSelector,
    showFileExplorer,
    setShowFileExplorer,
    // Actions
    sendMessage,
    checkConnection,
    fetchProjectFiles,
    createNewChat,
    loadChat,
    deleteChat
  };
};